import React, { useState, useEffect, useRef } from "react";
import { FaChevronDown } from "react-icons/fa";
import AiIcon from "../../assets/ai-icon.svg";
import '../../styles/components/resumeTailoring/suggestions.scss';
import '../../styles/components/resumeTailoring/animated-score.scss';
import "@radix-ui/themes/styles.css";
import CustomAccordion from "./CustomAccordion";
import { BulletPointSuggestion } from "./ResumeComponent";
import { IoChevronDownSharp } from "react-icons/io5"; // More modern arrow
import AnimatedScoreCircle from "./AnimatedScoreCircle";
import { useAI } from "./AIContext";

const truncateText = (text: string) => {
  const maxChars = 65; // Adjust this number based on testing
  if (text.length <= maxChars) return text;
  return text.slice(0, maxChars) + '...';
};

interface SuggestionsProps {
  suggestions: BulletPointSuggestion[];
  expandedIndex: string | null;
  handleToggleDropdown: (id: string) => void;
  isHovered: boolean;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  handleSingleSuggestion: (suggestion: string, id: string) => void;
  score: number | null;
  isFloatingScore?: boolean;
  handleTailorButtonClick: () => void;
  isResumeTailored?: boolean;
  handleDownload: () => void;
  setSuggestions: (suggestions: BulletPointSuggestion[]) => void;
  loading: boolean;
  errorMessage: string | null;
  isInExpandedSidebar?: boolean; // Add this new prop
  setExpandedBulletPoint: (point: { expIndex: number, descId: string } | null) => void;
}

const Suggestions: React.FC<SuggestionsProps> = ({
  suggestions,
  expandedIndex,
  handleToggleDropdown,
  isHovered,
  handleMouseEnter,
  handleMouseLeave,
  handleTailorButtonClick,
  handleSingleSuggestion,
  score,
  isFloatingScore = false,
  isResumeTailored = false,
  handleDownload,
  setSuggestions,
  loading,
  errorMessage,
  isInExpandedSidebar = false, // Add this new prop
  setExpandedBulletPoint,
}) => {
  const [isScrolling, setIsScrolling] = useState(false);
  const { setExpandedBulletPoint: setAIExpandedBulletPoint } = useAI();
  let scrollTimeout: NodeJS.Timeout;

  // Dynamic accordion height calculation
  const measureAccordionHeight = (accordionId: string) => {
    const accordionElement = document.querySelector(`[data-accordion-id="${accordionId}"] .accordion-content-inner`);
    if (accordionElement) {
      const height = accordionElement.scrollHeight;
      const accordionItem = document.querySelector(`[data-accordion-id="${accordionId}"]`);
      if (accordionItem) {
        (accordionItem as HTMLElement).style.setProperty('--accordion-height', `${height + 20}px`);
      }
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setIsScrolling(true);
    
    // Clear the previous timeout
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }
    
    // Set a new timeout to hide the scrollbar after scrolling stops
    scrollTimeout = setTimeout(() => {
      setIsScrolling(false);
    }, 1000); // Hide after 1 second of no scrolling
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, []);

  const handleDismissSuggestion = (suggestionId: string) => {
    const updatedSuggestions = suggestions.filter((item) => item.id !== suggestionId);
    setSuggestions(updatedSuggestions);
    setExpandedBulletPoint(null);
    setAIExpandedBulletPoint(null);
  };

  const handleApproveSuggestion = (suggestion: string, suggestionId: string) => {
    // First let the parent update resume and remove suggestion
    handleSingleSuggestion(suggestion, suggestionId);
    // Clear any highlighted bullet point in both local and AI context states
    setExpandedBulletPoint(null);
    setAIExpandedBulletPoint(null);
  };

  const handleAccordionChange = (id: string) => {
    // When accordion changes, find the corresponding suggestion
    const actualId = id.replace('item-', ''); // Remove the item- prefix if it exists
    const expandedSuggestion = suggestions.find(item => item.id === actualId || item.id === id);
    
    if (expandedSuggestion && id) {
      const bulletPointInfo = { 
        expIndex: expandedSuggestion.expIndex, // Keep the expIndex from the suggestion
        descId: expandedSuggestion.descId // Use the actual bullet point descId, not the suggestion UUID
      };
      
      // Update both the old state (for suggestions container) and new AI context (for bullet points)
      setExpandedBulletPoint(bulletPointInfo);
      setAIExpandedBulletPoint(bulletPointInfo);
      handleToggleDropdown(id);
      
      // Measure and set dynamic height with minimal delay for butter smooth response
      setTimeout(() => measureAccordionHeight(expandedSuggestion.id), 20);
    } else {
      setExpandedBulletPoint(null);
      setAIExpandedBulletPoint(null);
      handleToggleDropdown('');
    }
  };

  return (
    <>
      <div className={`suggestions-container ${!isInExpandedSidebar ? 'full-screen-mode' : ''}`}>
        {loading ? (
          <div className="loading-state">
            <h3 className="suggestions-title">
              <img src={AiIcon} alt="AI Icon" className="ai-icon" />
              Suggestions
              <div className="progress-circle">
                <div className="loading-circle">
                  <svg height={70} width={70}>
                    <circle
                      stroke="#CCCCCC"
                      fill="transparent"
                      strokeWidth={4}
                      r={31}
                      cx={35}
                      cy={35}
                    />
                  </svg>
                  <div className="loading-dots">
                    <span className="dot" />
                    <span className="dot" />
                    <span className="dot" />
                  </div>
                </div>
              </div>
            </h3>
            <div className="skeleton-suggestions">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="skeleton-suggestion">
                  <div className="skeleton-line long"></div>
                  <div className="skeleton-line short"></div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {isFloatingScore ? (
              <div className="floating-score-container">
                <AnimatedScoreCircle 
                  score={score} 
                  size={70} 
                  strokeWidth={4}
                  animationDuration={800}
                  isLoading={score === null}
                />
              </div>
            ) : (
              <>
                <h3 className="suggestions-title">
                  <img src={AiIcon} alt="AI Icon" className="ai-icon" />
                  Suggestions
                  <div className="progress-circle">
                    <AnimatedScoreCircle 
                      score={score} 
                      size={70} 
                      strokeWidth={4}
                      animationDuration={800}
                      isLoading={score === null}
                    />
                  </div>
                </h3>

                <div 
                  className={`suggestions-scroll ${isScrolling ? 'scrolling' : ''}`}
                  onScroll={handleScroll}
                >
                  <CustomAccordion
                    items={errorMessage ? [] : suggestions ? suggestions.map(item => ({
                      id: item.id,
                      suggestion: item.suggested
                    })) : []}
                    expandedId={expandedIndex}
                    onToggle={(id: string) => handleAccordionChange(id)}
                    onApprove={handleApproveSuggestion}
                    onDismiss={handleDismissSuggestion}
                    loading={loading}
                  />
                  {errorMessage && (
                    <div className="error-message">{errorMessage}</div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
      
      <div className="action-buttons-grid">
        <button className="action-btn primary" onClick={handleDownload}>
          Download PDF
        </button>
        <button className="action-btn secondary" onClick={handleTailorButtonClick}>
          Tailor Cover Letter
        </button>
      </div>
    </>
  );
};

export default Suggestions;